/**
 * Buffs — the pure power-up policy math (Brief 010).
 *
 * The Zelda-hearts + timed-invuln rules a power-up triggers, factored OUT of World
 * so they are unit-testable headless (no Pixi) AND the single source of truth World
 * composes. World stays the POLICY OWNER — it calls these and owns the side effects
 * (score, SFX, the live counter); these functions are side-effect-free integer math.
 *
 * Locked design (Isaac): a power-up is NEVER a hidden health layer. It acts on the
 * hearts meter (heal / capacity) or a timed buff (invuln) only.
 *
 * Determinism: `min` / `max` / `+ -` integer arithmetic only — ESLint-clean under
 * src/engine/ (no transcendentals / wall-clock / Math.random). Invuln is a fixed-step
 * counter so the same seed → the same expiry frame.
 */

/** Hard cap on `maxHearts` for the extra-heart container (P4 — matches the editor's
 *  startHearts 1–9 range so authored + found capacity share one ceiling). */
export const MAX_HEARTS_CAP = 9;

/** Score awarded when a heart-refill is collected at full hearts (P2 — never wasted). */
export const FULL_HEART_SCORE_BONUS = 50;

/** A read-only view of the hearts meter the buff math operates over. */
export interface HeartState {
  hearts: number;
  maxHearts: number;
}

/**
 * Apply a heart-refill of `n`. Clamps to `maxHearts`. `wasted` is true when the meter
 * was already full (caller awards {@link FULL_HEART_SCORE_BONUS} instead of healing).
 */
export function applyHeal(s: HeartState, n: number): { hearts: number; wasted: boolean } {
  if (s.hearts >= s.maxHearts) return { hearts: s.maxHearts, wasted: true };
  return { hearts: Math.min(s.maxHearts, s.hearts + n), wasted: false };
}

/**
 * Apply an extra-heart container: raise `maxHearts` by one (capped at
 * {@link MAX_HEARTS_CAP}) and fill the new heart. At the cap it is a no-op for
 * capacity but still tops the meter off — `gained` reports whether capacity rose.
 */
export function applyAddMaxHeart(s: HeartState, cap = MAX_HEARTS_CAP): { hearts: number; maxHearts: number; gained: boolean } {
  if (s.maxHearts >= cap) return { hearts: s.maxHearts, maxHearts: s.maxHearts, gained: false };
  const maxHearts = s.maxHearts + 1;
  return { hearts: maxHearts, maxHearts, gained: true };
}

/**
 * Grant/refresh an invuln window: take the longer of the current remaining steps and
 * the new grant (refresh-and-extend, never stack unboundedly — P5 edge rule).
 */
export function grantInvuln(currentSteps: number, steps: number): number {
  return Math.max(currentSteps, Math.max(0, Math.floor(steps)));
}

/** Tick an invuln counter one fixed step: count down to 0, never below. */
export function tickInvuln(currentSteps: number): number {
  return currentSteps > 0 ? currentSteps - 1 : 0;
}

/** Convert a duration in ms to whole fixed steps at 60 Hz (deterministic; integer). */
export function msToSteps(ms: number): number {
  return Math.max(0, Math.round((ms / 1000) * 60));
}
